import { t } from '@lingui/macro'
import { Project } from '@lune-climate/lune'
import { LuneThemeNew, NoResultsPlaceholder, Text } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import { Chart as ChartJS } from 'chart.js/auto'
import { abbreviateNumber } from 'js-abbreviation-number'

import { formatNumbers, formatToCurrency } from '../../utils'

export interface Dataset {
    date: string
    quantity: string
    value: string
}

const { palette, typography, shadows } = LuneThemeNew

export const getOrCreateTooltip = (chart: ChartJS) => {
    if (chart.canvas.parentNode !== null) {
        let tooltipEl = chart.canvas.parentNode.querySelector('div')
        if (!tooltipEl) {
            tooltipEl = document.createElement('div')
            tooltipEl.style.background = palette.Surface100
            tooltipEl.style.borderRadius = '8px'
            tooltipEl.style.color = palette.TextPrimary
            tooltipEl.style.opacity = '1'
            tooltipEl.style.pointerEvents = 'none'
            tooltipEl.style.position = 'absolute'
            tooltipEl.style.transition = 'all .1s ease'
            tooltipEl.style.fontSize = '12px'
            tooltipEl.style.fontFamily = typography.fontFamily as any
            tooltipEl.style.minWidth = '120px'
            tooltipEl.style.boxShadow = shadows[7]
            tooltipEl.style.width = 'auto'
            tooltipEl.style.maxWidth = 'none'

            const table = document.createElement('table')
            table.style.margin = '0'

            tooltipEl.appendChild(table)
            chart.canvas.parentNode.appendChild(tooltipEl)
        }
        return tooltipEl
    }
}

const createTooltipElement = (textString: string, color?: string): HTMLTableRowElement => {
    const span = document.createElement('span')
    span.style.display = 'none' as const

    const tr = document.createElement('tr')
    tr.style.backgroundColor = palette.Surface100
    tr.style.borderWidth = '0'
    tr.style.fontSize = '12px'

    const td = document.createElement('td')
    td.style.borderWidth = '0'
    td.style.display = 'flex'
    td.style.alignItems = 'center'
    td.style.gap = '8px'
    td.style.fontSize = '12px'
    td.style.lineHeight = '24px'
    td.style.fontFamily = typography.fontFamily as string

    // Add color indicator if color is provided
    if (color) {
        const colorCircle = document.createElement('span')
        colorCircle.style.width = '12px'
        colorCircle.style.height = '12px'
        colorCircle.style.borderRadius = '50%'
        colorCircle.style.backgroundColor = color
        colorCircle.style.flexShrink = '0'
        td.appendChild(colorCircle)
    }

    const text = document.createTextNode(textString)
    td.appendChild(span)
    td.appendChild(text)
    tr.appendChild(td)

    return tr
}

export type TooltipHeaderItem = {
    label: string
    value: number
}

export const externalTooltipHandler = (
    context: any,
    {
        label,
        showTooltipLabels = false,
        skipZeroOnTooltip = false,
        orderedLabelTranslation = [],
        reverseTooltipOrder = false,
        carbonStorageOverTimeData = [],
        isCarbonStorage = false,
        dataColors,
        showTooltipColors = false,
        currency,
        getTooltipHeaderItems,
    }: {
        label?: string
        showTooltipLabels?: boolean
        skipZeroOnTooltip?: boolean
        orderedLabelTranslation?: string[]
        reverseTooltipOrder?: boolean
        carbonStorageOverTimeData?: NonNullable<Project[`carbonStorageOverTime`]>[0][]
        isCarbonStorage?: boolean
        dataColors?: Record<string, string>
        showTooltipColors?: boolean
        currency?: string
        getTooltipHeaderItems?: (date: string) => TooltipHeaderItem[]
    } = {},
) => {
    // Tooltip Element
    const { chart, tooltip } = context
    const tooltipEl = getOrCreateTooltip(chart)

    // Hide if no tooltip
    if (tooltipEl && tooltip.opacity === 0) {
        tooltipEl.style.opacity = '0'
        return
    }

    // Set Text
    if (tooltip.body) {
        const titleLines = tooltip.title || []
        const bodyLinesRaw: any[] = tooltip.body.map((b: any) => b.lines)
        const bodyLines = reverseTooltipOrder ? bodyLinesRaw.reverse() : bodyLinesRaw

        // Carbon Storage Specific Tooltip
        if (isCarbonStorage) {
            const year = tooltip.dataPoints[0].label
            const datapointByYear = carbonStorageOverTimeData.find(
                (d) => d.year.toString() === year,
            )!

            const head = document.createElement('div')
            titleLines.forEach((title: string) => {
                const titleDiv = document.createElement('div')
                titleDiv.style.fontWeight = '400'
                titleDiv.style.marginBottom = '8px'
                titleDiv.style.color = palette.TextSecondary
                titleDiv.style.fontSize = '12px'
                const text = document.createTextNode(title)
                titleDiv.appendChild(text)
                head.appendChild(titleDiv)
            })

            const tableRoot = tooltipEl?.querySelector('table')
            const body = document.createElement('div')
            body.style.fontSize = '12px'
            body.style.fontFamily = typography.fontFamily as string

            // Remove old children
            while (tableRoot?.firstChild) {
                tableRoot.firstChild.remove()
            }

            // Add new children
            const baseline = document.createElement('div')
            baseline.appendChild(
                document.createTextNode(
                    t`Baseline projection: ${formatNumbers(datapointByYear.baseline)} tCO₂`,
                ),
            )
            baseline.style.marginBottom = '2px'
            const carbonStock = document.createElement('div')
            carbonStock.appendChild(
                document.createTextNode(
                    t`Project carbon stock: ${formatNumbers(datapointByYear.carbonStock)} tCO₂`,
                ),
            )
            carbonStock.style.marginBottom = '2px'
            const buffer = document.createElement('div')
            buffer.appendChild(
                document.createTextNode(t`Buffer: ${formatNumbers(datapointByYear.buffer)} tCO₂`),
            )
            buffer.style.marginBottom = '2px'
            const assumedLeakage = document.createElement('div')
            assumedLeakage.appendChild(
                document.createTextNode(
                    t`Assumed leakage: ${formatNumbers(datapointByYear.assumedLeakage)} tCO₂`,
                ),
            )
            assumedLeakage.style.marginBottom = '2px'
            const creditsIssued = document.createElement('div')
            creditsIssued.appendChild(
                document.createTextNode(
                    t`Credits issued: ${formatNumbers(datapointByYear.creditsIssued)} tCO₂`,
                ),
            )
            creditsIssued.style.marginBottom = '2px'

            body.appendChild(head)
            body.appendChild(carbonStock)
            body.appendChild(baseline)
            body.appendChild(buffer)
            body.appendChild(assumedLeakage)
            body.appendChild(creditsIssued)
            tableRoot?.appendChild(body)
        } else {
            // Generic Tooltip
            const tableRoot = tooltipEl?.querySelector('table')

            // Remove old children
            while (tableRoot?.firstChild) {
                tableRoot.firstChild.remove()
            }

            // Add date header
            if (titleLines.length > 0) {
                titleLines.forEach((title: string) => {
                    const headerRow = document.createElement('tr')
                    headerRow.style.borderWidth = '0'

                    const headerCell = document.createElement('th')
                    headerCell.style.borderWidth = '0'
                    headerCell.style.fontSize = '12px'
                    headerCell.style.fontWeight = '400'
                    headerCell.style.fontFamily = typography.fontFamily as string
                    headerCell.style.color = palette.TextSecondary
                    headerCell.style.paddingBottom = '8px'
                    headerCell.style.textAlign = 'left'
                    const text = document.createTextNode(title)

                    headerCell.appendChild(text)
                    headerRow.appendChild(headerCell)
                    tableRoot?.appendChild(headerRow)
                })
            }

            // Add dynamic header items if provided (using the date from the tooltip)
            if (getTooltipHeaderItems && titleLines.length > 0) {
                const tooltipDate = titleLines[0]
                const headerItems = getTooltipHeaderItems(tooltipDate)
                headerItems.forEach((item) => {
                    const itemRow = document.createElement('tr')
                    itemRow.style.borderWidth = '0'

                    const itemCell = document.createElement('td')
                    itemCell.style.borderWidth = '0'
                    itemCell.style.fontSize = '12px'
                    itemCell.style.fontWeight = '400'
                    itemCell.style.fontFamily = typography.fontFamily as string
                    itemCell.style.color = palette.TextPrimary
                    itemCell.style.paddingBottom = '8px'
                    itemCell.style.textAlign = 'left'
                    const itemText = document.createTextNode(
                        `${item.label}: ${formatNumbers(item.value, 2)} ${label || ''}`.trim(),
                    )

                    itemCell.appendChild(itemText)
                    itemRow.appendChild(itemCell)
                    tableRoot?.appendChild(itemRow)
                })
            }

            const tableBody = document.createElement('tbody')
            // eslint-disable-next-line complexity
            bodyLines.forEach((_body: string | string[], index: number) => {
                const labelIndex = reverseTooltipOrder ? bodyLines.length - index - 1 : index

                const dataPoint = tooltip.dataPoints[labelIndex]
                const parsed = dataPoint.parsed
                const numericValue =
                    typeof parsed === 'number'
                        ? parsed
                        : chart.options.indexAxis === 'y'
                          ? parsed.x
                          : parsed.y

                if (skipZeroOnTooltip && numericValue === 0) {
                    return
                }

                const labelText = orderedLabelTranslation.length
                    ? orderedLabelTranslation[labelIndex]
                    : undefined
                const formattedValue = currency
                    ? formatToCurrency(Math.abs(numericValue), currency)
                    : formatNumbers(numericValue)
                const datasetLabel = dataPoint.dataset.label
                const formattedBody =
                    !currency && !labelText && datasetLabel
                        ? `${datasetLabel}: ${formattedValue}`
                        : formattedValue

                const text = labelText
                    ? `${labelText}: ` +
                      formattedBody +
                      `${showTooltipLabels && !currency ? ` ${label}` : ''}`
                    : formattedBody + ` ${!currency ? label : ''}`

                // Get color from dataColors if available and showTooltipColors is true
                const color =
                    showTooltipColors && dataColors && labelText ? dataColors[labelText] : undefined

                tableBody.appendChild(createTooltipElement(text, color))
            })

            if (tableBody.children.length === 0) {
                tableBody.appendChild(createTooltipElement(t`No emissions`))
            }

            tableRoot?.appendChild(tableBody)
        }
    }

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas

    // Display, position, and set styles for font
    if (tooltipEl) {
        tooltipEl.style.opacity = '1'
        tooltipEl.style.font = typography.fontFamily as any
        tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px'
        const caretX = positionX + tooltip.caretX
        const caretY = positionY + tooltip.caretY
        tooltipEl.style.left = `${caretX}px`
        tooltipEl.style.top = `${caretY}px`
        tooltipEl.style.transform = 'translate(-50%, -100%)'
        tooltipEl.style.marginTop = '-10px'
        tooltipEl.style.visibility = 'hidden'
        void tooltipEl.offsetWidth
        tooltipEl.style.visibility = 'visible'
    }
}

// Shared Y-axis configuration
export const getYAxisScaleConfig = (label: string, maxDatapointValue?: number) => ({
    title: {
        display: true,
        text: label,
        color: palette.TextPrimary,
        font: {
            family: typography.fontFamily,
            size: '12px',
            style: 'normal',
            lineHeight: '14px',
        },
        padding: { top: 0, left: 0, right: 0, bottom: 0 },
    },
    stacked: true,
    grid: {
        tickLength: 0,
        drawBorder: false,
        color: [palette.Grey250],
        tickBorderDashOffset: 0,
    },
    suggestedMax: maxDatapointValue ? maxDatapointValue * 1.2 : undefined,
    ticks: {
        z: 9 as const,
        padding: 10 as const,
        showLabelBackdrop: true,
        backdropColor: 'transparent',
        textStrokeWidth: 10,
        color: palette.TextSecondary,
        font: {
            family: typography.fontFamily,
        },
        callback: (value: any, index: any, ticks: any) => {
            if (index === ticks.length - 1) {
                return null
            } else {
                const numValue = typeof value === 'string' ? parseFloat(value) : value
                // Round to 2 decimals to fix floating point precision issues (e.g., 12.200000000000001 -> 12.2)
                const roundedValue = Number(numValue.toFixed(2))
                return abbreviateNumber(roundedValue, 2, { padding: false })
            }
        },
    },
    border: {
        color: 'transparent',
    },
})

// Shared x-axis configuration
export const getXAxisScaleConfig = () => ({
    stacked: true,
    ticks: {
        beginAtZero: true,
        font: {
            family: typography.fontFamily,
        },
        color: palette.TextSecondary,
    },
    grid: {
        display: false,
    },
})

// Chart options
export const barChartOptions = (
    label: string,
    showTooltipLabels: boolean,
    maxDatapointValue?: number,
    skipZeroOnTooltip?: boolean,
    orderedLabelTranslation?: string[],
    reverseTooltipOrder?: boolean,
    dataColors?: Record<string, string>,
    showTooltipColors?: boolean,
    getTooltipHeaderItems?: (date: string) => TooltipHeaderItem[],
    padLeft?: boolean,
) => {
    return {
        maintainAspectRatio: false,
        layout: {
            autoPadding: false,
            padding: { left: padLeft ? 24 : 0 },
        },
        scales: {
            y: getYAxisScaleConfig(label, maxDatapointValue),
            x: {
                ...getXAxisScaleConfig(),
                categoryPercentage: 0.8,
                barPercentage: 0.9,
            },
        },
        plugins: {
            paddingBelowLegends: '30px',
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,
                position: 'nearest' as const,
                external: (c: any) =>
                    externalTooltipHandler(c, {
                        label,
                        showTooltipLabels,
                        skipZeroOnTooltip,
                        orderedLabelTranslation,
                        reverseTooltipOrder,
                        dataColors,
                        showTooltipColors,
                        getTooltipHeaderItems,
                    }),
                mode: 'index',
                intersect: false,
            },
            chartAreaBorder: {
                borderColor: palette.Grey250,
                borderWidth: 1,
            },
        },
    }
}

export const EmptyGraph = () => {
    return (
        <Box sx={{ height: '414px', width: '100%' }}>
            <NoResultsPlaceholder
                message={
                    <Text variant={'h6'} sx={{ textAlign: 'center', maxWidth: '250px' }}>
                        {t`You don't have any data for the selected time period`}
                    </Text>
                }
            />
        </Box>
    )
}
